import strings from "global/constants/StringConstants";
import { fillCopy, isUnlimitedLimit } from "helpers/methods";
import { PromoCampaign, promoPercentFor } from "helpers/promotionHelpers";
import {
  getBillingInterval,
  getPlanSeatLimit,
  isPerSeatPlan,
} from "helpers/perSeatHelpers";
import { formatPlanAmount } from "./MyPlanHelper";

/**
 * Pure view-model logic for Settings → My Plan → "Choose your plan".
 *
 * The plan-picker is entirely derived: the seat count the customer dials in is
 * the number of seats they are buying, and it decides which plans stay
 * eligible, which one is recommended, and what each card shows for price,
 * billing note and totals. All of that lives here so the components stay
 * presentational.
 */

/** Used when the plan list carries no annual discount to derive a badge from. */
export const DEFAULT_ANNUAL_SAVINGS_PERCENT = 25;

export interface UpgradePlanCard {
  id: string;
  name: string;
  /** Formatted per-seat headline, e.g. "$35". */
  price: string;
  /** Unit next to the headline, e.g. "/seat/mo". */
  per: string;
  /** "billed monthly" / "billed annually ($336/yr for 4)"; empty for free. */
  cycleNote: string;
  blurb: string;
  /** Seat rule pill, e.g. "Up to 5 users". */
  rule: string;
  bullets: string[];
  /** "4 seats × $35 = $140/mo"; empty when the card shouldn't show a total. */
  totalNote: string;
  /** Why the plan can't be picked; empty when it can. */
  note: string;
  isEligible: boolean;
  isCurrent: boolean;
  isRecommended: boolean;
  /** Availability chip at the top of the card. */
  chip: { label: string; tone: "current" | "available" | "unavailable" };
  /**
   * Seasonal discount on this card, when one is being priced. `price`,
   * `cycleNote` and `totalNote` above are then already the discounted figures.
   */
  promo?: {
    /** Undiscounted per-seat headline, shown struck through. */
    regularPrice: string;
    /** "70% off · First year". */
    pill: string;
    /** "Then $X/year at the regular price". */
    renew: string;
  };
  /** Raw plan payload, handed straight to the checkout step. */
  plan: any;
}

/** The seasonal campaign running for this viewer, and how it is charged. */
export interface UpgradePlanPromo {
  campaign: PromoCampaign;
  /**
   * Whether the backend applies the sale code when this account moves to
   * `plan`. It only does so on a first purchase or a switch that recreates
   * the subscription, never on the in-place per-seat switch, and never while
   * the older festival-offer schedule (`offerEnabled`) is on.
   */
  isChargeable: (plan: any) => boolean;
  /**
   * Non-prod `?promoPrices=1`: when the catalog carries no sale price yet,
   * derive one from the campaign percentage so the design can be reviewed.
   */
  preview?: boolean;
}

/** A campaign price on one plan, per seat per month. */
export interface PlanSale {
  unit: number;
  regular: number;
  percent: number;
}

const roundCents = (value: number) => Math.round(value * 100) / 100;

/**
 * The campaign sale the catalog is pricing this plan at, if any.
 *
 * The backend writes the active sale code's price into `discountPrice`
 * (`AppService.activePromotionCode`, from the plan's dated
 * `promotionSchedule`). Annual plans carry an everyday `discountPrice` too, so
 * a value only counts as the campaign once its saving matches the advertised
 * percentage — otherwise it is the standing annual rate, not the sale.
 */
const getCatalogSale = (
  plan: any,
  isAnnual: boolean,
  campaign: PromoCampaign,
): PlanSale | null => {
  const regular = Number(plan?.price) || 0;
  const unit = Number(plan?.discountPrice) || 0;
  if (regular <= 0 || unit <= 0 || unit >= regular) return null;
  const percent = Math.round((1 - unit / regular) * 100);
  return Math.abs(percent - promoPercentFor(campaign, isAnnual)) <= 1
    ? { unit, regular, percent }
    : null;
};

/** The sale the catalog (or the non-prod preview) puts on a plan. */
const getListedSale = (
  plan: any,
  isAnnual: boolean,
  promo: UpgradePlanPromo,
): PlanSale | null => {
  const listed = getCatalogSale(plan, isAnnual, promo.campaign);
  if (listed || !promo.preview) return listed;
  const regular = Number(plan?.price) || 0;
  if (regular <= 0) return null;
  const percent = promoPercentFor(promo.campaign, isAnnual);
  return { unit: roundCents(regular * (1 - percent / 100)), regular, percent };
};

/**
 * The sale this account is actually charged on `plan` — what the plan card
 * and checkout quote. Null when there's no sale, or the backend won't apply it
 * to this purchase.
 */
export const getPlanSale = (
  plan: any,
  isAnnual: boolean,
  promo?: UpgradePlanPromo | null,
): PlanSale | null => {
  if (!promo || !plan?.id || !promo.isChargeable(plan)) return null;
  return getListedSale(plan, isAnnual, promo);
};

/**
 * Per-seat monthly rate to quote for a plan. A campaign price this account
 * won't get falls back to the regular price — the catalog's `discountPrice`
 * holds the sale price for the whole window, so quoting it as-is would
 * advertise a discount the switch doesn't apply.
 */
export const getQuotedPerSeat = (
  plan: any,
  isAnnual: boolean,
  promo?: UpgradePlanPromo | null,
): number => {
  if (!promo) return getPerSeatMonthly(plan, isAnnual);
  const sale = getPlanSale(plan, isAnnual, promo);
  if (sale) return sale.unit;
  return getCatalogSale(plan, isAnnual, promo.campaign)
    ? Number(plan?.price) || 0
    : getPerSeatMonthly(plan, isAnnual);
};

/**
 * Splits a flat plan catalog into the two billing-cycle tabs. Plans on neither
 * cadence (a lifetime free tier) belong to both, so the picker keeps the same
 * columns whichever tab is showing.
 */
export const groupPlansByCycle = (plans: any[]) => {
  const list = plans ?? [];
  const isYearly = (plan: any) =>
    getBillingInterval(plan?.validityInDays) === "yearly";
  const isMonthly = (plan: any) =>
    getBillingInterval(plan?.validityInDays) === "monthly";
  return {
    Monthly: list.filter((plan: any) => !isYearly(plan)),
    Annual: list.filter((plan: any) => !isMonthly(plan)),
  };
};

/**
 * Seat cap of a plan for the picker, where a plan that states no cap is treated
 * as single-seat so it can't swallow a whole team by accident.
 */
export const getPlanSeatCap = (plan: any): number =>
  getPlanSeatLimit(plan) || 1;

/**
 * Per-seat monthly rate. Annual plan entries carry the discounted rate in
 * `discountPrice`; monthly ones bill at `price`.
 */
export const getPerSeatMonthly = (plan: any, isAnnual: boolean): number => {
  const base = Number(plan?.price) || 0;
  const discounted = Number(plan?.discountPrice) || 0;
  return isAnnual && discounted > 0 && discounted < base ? discounted : base;
};

/** Whole-percent saving the annual plans offer over their monthly rate. */
export const getAnnualSavingsPercent = (annualPlans: any[]): number => {
  const discounted = (annualPlans ?? []).find((plan: any) => {
    const base = Number(plan?.price) || 0;
    const rate = Number(plan?.discountPrice) || 0;
    return base > 0 && rate > 0 && rate < base;
  });
  if (!discounted) return DEFAULT_ANNUAL_SAVINGS_PERCENT;
  const base = Number(discounted.price);
  const rate = Number(discounted.discountPrice);
  return Math.round((1 - rate / base) * 100);
};

const seatWord = (seats: number) => (seats === 1 ? "seat" : "seats");

/** "1 user" / "Up to 5 users" / "More than 5 users" for the uncapped top tier. */
const getSeatRule = (cap: number, previousCap: number): string => {
  if (isUnlimitedLimit(cap)) {
    return previousCap > 0 && !isUnlimitedLimit(previousCap)
      ? `More than ${previousCap} users`
      : "Unlimited users";
  }
  return cap === 1 ? "1 user" : `Up to ${cap} users`;
};

export interface UpgradePlanCardsInput {
  /** Plans for the selected billing cycle, as returned by the plans API. */
  plans: any[];
  isAnnual: boolean;
  seats: number;
  currentPlanId?: string;
  /**
   * The app-level per-seat gate. Per-seat needs both this and the plan's own
   * metadata; with the app flag off every plan is priced flat.
   */
  isPerSeatApp?: boolean;
  /** Seasonal campaign running for this viewer — see `getPlanSale`. */
  promo?: UpgradePlanPromo | null;
}

/** Builds one view model per plan column, cheapest first. */
export const buildUpgradePlanCards = ({
  plans,
  isAnnual,
  seats,
  currentPlanId,
  isPerSeatApp = true,
  promo = null,
}: UpgradePlanCardsInput): UpgradePlanCard[] => {
  const sorted = [...(plans ?? [])].sort(
    (a: any, b: any) => (Number(a?.price) || 0) - (Number(b?.price) || 0),
  );
  const caps = sorted.map(getPlanSeatCap);

  // The recommendation is the cheapest paid plan that still covers the team,
  // skipping the plan the account is already on.
  const recommendedIndex = sorted.findIndex(
    (plan: any, index: number) =>
      (Number(plan?.price) || 0) > 0 &&
      caps[index] >= seats &&
      plan?.id !== currentPlanId,
  );

  return sorted.map((plan: any, index: number) => {
    const cap = caps[index];
    const isFree = (Number(plan?.price) || 0) === 0;
    const isEligible = seats <= cap;
    const isCurrent = Boolean(currentPlanId) && plan?.id === currentPlanId;
    const sale =
      isEligible && !isCurrent && !isFree
        ? getPlanSale(plan, isAnnual, promo)
        : null;
    const perSeat = getQuotedPerSeat(plan, isAnnual, promo);
    // Only a per-seat plan multiplies its price by the seats being bought, and
    // only while the app itself is on per-seat billing — both gates, as the
    // backend applies them. Everything else bills flat at quantity 1 (§7).
    const billsPerSeat = isPerSeatApp && isPerSeatPlan(plan);
    const hasSeatPricing = billsPerSeat && (cap > 1 || isUnlimitedLimit(cap));
    const billedSeats = billsPerSeat ? seats : 1;

    let cycleNote = "";
    if (!isFree) {
      if (!isAnnual) {
        cycleNote = "billed monthly";
      } else if (isEligible) {
        const yearly = formatPlanAmount(
          Math.round(perSeat * 12 * billedSeats * 100) / 100,
        );
        cycleNote = `billed annually ($${yearly}/yr${
          hasSeatPricing ? ` for ${seats}` : ""
        })`;
      } else {
        cycleNote = "billed annually";
      }
    }

    const showTotal = isEligible && hasSeatPricing && !isFree;
    const seatCount = `${seats} ${seatWord(seats).toUpperCase()}`;
    const chip: UpgradePlanCard["chip"] = isCurrent
      ? { label: strings.upgradePlan.chipCurrent, tone: "current" }
      : isEligible
        ? {
            label: fillCopy(strings.upgradePlan.chipAvailable, { seats: seatCount }),
            tone: "available",
          }
        : {
            label: fillCopy(strings.upgradePlan.chipUnavailable, { seats: seatCount }),
            tone: "unavailable",
          };

    // The discount only applies to a plan the customer can actually move to.
    if (sale) {
      const { unit, regular, percent } = sale;
      const months = isAnnual ? 12 : 1;
      const qty = billsPerSeat
        ? `${seats} ${seatWord(seats)} × `
        : "";
      const copy = strings.promotions.plan;
      return {
        id: plan?.id,
        name: plan?.name ?? "",
        price: `$${formatPlanAmount(unit)}`,
        per: billsPerSeat ? "/seat/mo" : "/mo",
        cycleNote: isAnnual ? copy.billedAnnual : copy.billedMonthly,
        blurb: plan?.description ?? "",
        rule: getSeatRule(cap, index > 0 ? caps[index - 1] : 0),
        bullets: plan?.feature ?? plan?.features ?? [],
        totalNote: fillCopy(isAnnual ? copy.totalAnnual : copy.totalMonthly, {
          qty,
          unit: `$${formatPlanAmount(unit)}`,
          total: `$${formatPlanAmount(roundCents(unit * months * billedSeats))}`,
        }),
        note: "",
        isEligible,
        isCurrent,
        isRecommended: index === recommendedIndex,
        chip,
        promo: {
          regularPrice: `$${formatPlanAmount(regular)}`,
          pill: fillCopy(isAnnual ? copy.cycleAnnual : copy.cycleMonthly, {
            pct: percent,
          }),
          renew: fillCopy(isAnnual ? copy.renewAnnual : copy.renewMonthly, {
            total: `$${formatPlanAmount(
              roundCents(regular * months * billedSeats),
            )}`,
          }),
        },
        plan,
      };
    }

    return {
      id: plan?.id,
      name: plan?.name ?? "",
      price: `$${formatPlanAmount(perSeat)}`,
      per: isFree ? "/forever" : billsPerSeat ? "/seat/mo" : "/mo",
      cycleNote,
      blurb: plan?.description ?? "",
      rule: getSeatRule(cap, index > 0 ? caps[index - 1] : 0),
      bullets: plan?.feature ?? plan?.features ?? [],
      totalNote: showTotal
        ? `${seats} ${seatWord(seats)} × $${formatPlanAmount(
            perSeat,
          )} = $${formatPlanAmount(
            Math.round(seats * perSeat * 100) / 100,
          )}/mo`
        : "",
      note: isEligible
        ? ""
        : `${
            cap === 1 ? "Single-user plan" : `Up to ${cap} users`
          } — your team has ${seats}`,
      isEligible,
      isCurrent,
      isRecommended: index === recommendedIndex,
      chip,
      plan,
    };
  });
};

/**
 * Line under "How many people will use Inkfree?" — names the cheapest plan that
 * still covers the dialled-in seat count.
 */
export const buildSeatHint = (plans: any[], seats: number): string => {
  const sorted = [...(plans ?? [])].sort(
    (a: any, b: any) => (Number(a?.price) || 0) - (Number(b?.price) || 0),
  );
  if (sorted.length === 0) return "";
  const caps = sorted.map(getPlanSeatCap);
  const fitIndex = caps.findIndex((cap: number) => cap >= seats);
  if (fitIndex < 0) return "";

  if (seats === 1) {
    const names = sorted.slice(0, 2).map((plan: any) => plan?.name ?? "");
    return names.length > 1
      ? `Just you — ${names[0]} or ${names[1]} will do.`
      : `Just you — ${names[0]} will do.`;
  }

  const fitName = sorted[fitIndex]?.name ?? "";
  if (isUnlimitedLimit(caps[fitIndex])) {
    const previousCap = fitIndex > 0 ? caps[fitIndex - 1] : 0;
    return previousCap > 0
      ? `More than ${previousCap} people — ${fitName} is required.`
      : `${fitName} is required.`;
  }
  return `Up to ${caps[fitIndex]} people — ${fitName} covers your team.`;
};

/**
 * Id of the listed plan the account is already on, within the cycle currently
 * being viewed. Matches on id first, then on name (the same tier can have a
 * different id per billing cycle) — but the name fallback only matches inside
 * the account's own billing cycle. Business **monthly** must not read as the
 * "current plan" on the Annual tab's Business card: that's a different price
 * and commitment, and disabling it as if it were already bought would block a
 * legitimate monthly-to-annual switch.
 */
export const findMatchingPlanId = (
  plans: any[],
  currentPlan: any,
): string | undefined => {
  if (!currentPlan) return undefined;
  const byId = (plans ?? []).find(
    (plan: any) => plan?.id && plan.id === currentPlan?.id,
  );
  if (byId) return byId.id;
  const name = String(currentPlan?.name ?? "")
    .trim()
    .toLowerCase();
  if (!name) return undefined;
  const currentCycle = getBillingInterval(currentPlan?.validityInDays);
  return (plans ?? []).find(
    (plan: any) =>
      String(plan?.name ?? "")
        .trim()
        .toLowerCase() === name &&
      getBillingInterval(plan?.validityInDays) === currentCycle,
  )?.id;
};

export interface SeatUpdateBanner {
  title: string;
  description: string;
  cta: string;
}

/*
 * There is intentionally no builder for `SeatUpdateBanner`. Changing the seat
 * count on a live subscription is its own action — "Manage seats", which posts
 * to `/billing/update/seats/{count}` from the Team and My Plan screens — so the
 * picker has nothing to offer beyond choosing the plan and the seats to buy.
 */
